/**
 * Zone blend state: a single scalar weight crossfaded between outdoor (1)
 * and indoor (0), plus a global finale dim. Frame-rate independent
 * exponential settle driven by loop dt.
 * Owned by LIGHT; see CONTRACTS.md.
 */
import type { ZoneId } from '../core/types'
import { ZONE_FADE_RATE } from './constants'

const OUTDOOR_WEIGHT = 1
const INDOOR_WEIGHT = 0
const FULL_DIM = 1

export interface ZoneBlend {
  /** Advance the crossfade; call once per fixed update. */
  update(dt: number): void
  setZone(zone: ZoneId): void
  setGlobalDim(dim: number): void
  /** 1 = fully outdoor mood, 0 = fully indoor mood. */
  weight(): number
  /** Finale multiplier applied to every rig (1 = normal). */
  globalDim(): number
}

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t

export const createZoneBlend = (initialZone: ZoneId): ZoneBlend => {
  let target = initialZone === 'outdoor' ? OUTDOOR_WEIGHT : INDOOR_WEIGHT
  let current = target
  let dimTarget = FULL_DIM
  let dim = FULL_DIM

  const update = (dt: number): void => {
    const k = Math.min(1, dt * ZONE_FADE_RATE)
    current = lerp(current, target, k)
    dim = lerp(dim, dimTarget, k)
  }

  return {
    update,
    setZone: (zone) => {
      target = zone === 'outdoor' ? OUTDOOR_WEIGHT : INDOOR_WEIGHT
    },
    setGlobalDim: (value) => {
      dimTarget = value
    },
    weight: () => current,
    globalDim: () => dim
  }
}
